import React, { useRef, useEffect } from 'react';
import { Box, useTheme } from '@mui/material';

/**
 * Componente para visualização de áudio em tempo real usando Canvas
 *
 * @param {Object} props
 * @param {MediaStream} props.stream - Stream de mídia do microfone
 * @param {number} props.barCount - Número de barras a serem exibidas (padrão: 32)
 * @param {string} props.barColor - Cor das barras (padrão: cor primária do tema)
 * @param {number} props.height - Altura do visualizador em pixels (padrão: 50)
 * @param {number} props.barWidth - Largura de cada barra em pixels (padrão: 6)
 * @param {number} props.gap - Espaçamento entre as barras em pixels (padrão: 8)
 */
const AudioVisualizer = ({ stream, barCount = 24, barColor, height = 50, barWidth = 6, gap = 8, className, style }) => {
    const theme = useTheme();
    const canvasRef = useRef(null);
    const animationRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const dataArrayRef = useRef(null);

    // Cor padrão das barras é a cor primária do tema
    const color = barColor || theme.palette.primary.main;

    // Calcula a largura total com base no número de barras
    const totalWidth = barCount * (barWidth + gap) - gap;

    useEffect(() => {
        if (!stream) return;

        // Limpar qualquer contexto de áudio anterior
        if (audioContextRef.current) {
            cancelAnimationFrame(animationRef.current);
            audioContextRef.current.close();
        }

        // Configuração do analisador de áudio
        const audioContext = new AudioContext();
        audioContextRef.current = audioContext;

        const analyser = audioContext.createAnalyser();
        const source = audioContext.createMediaStreamSource(stream);

        // Aumentar a precisão da FFT para melhor distribuição dos dados
        analyser.fftSize = 2048;
        analyser.smoothingTimeConstant = 0.7;

        source.connect(analyser);
        analyserRef.current = analyser;

        // Criar o array de dados de frequência
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        dataArrayRef.current = dataArray;

        // Iniciar a visualização
        startVisualization();

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            if (audioContext.state !== 'closed') {
                audioContext.close();
            }
        };
    }, [stream, barCount]);

    useEffect(() => {
        // Redimensionar o canvas quando as dimensões mudam
        const canvas = canvasRef.current;
        if (canvas) {
            canvas.width = totalWidth;
            canvas.height = height;

            // Se o analisador já estiver configurado, reiniciar a visualização
            if (analyserRef.current && dataArrayRef.current) {
                startVisualization();
            }
        }
    }, [totalWidth, height]);

    const startVisualization = () => {
        if (!analyserRef.current || !dataArrayRef.current || !canvasRef.current) return;

        const analyser = analyserRef.current;
        const dataArray = dataArrayRef.current;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        const bufferLength = analyser.frequencyBinCount;

        // Função para desenhar as barras de frequência
        const draw = () => {
            // Solicitar o próximo frame antes de processar o atual (melhora a performance)
            animationRef.current = requestAnimationFrame(draw);

            // Obter os dados de frequência atualizados
            analyser.getByteFrequencyData(dataArray);

            // Limpar o canvas
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Desenhar as barras
            for (let i = 0; i < barCount; i++) {
                // Calcular índice de frequência para distribuição logarítmica
                // Isso favorece frequências baixas/médias que são mais importantes no áudio de fala
                const logIndex = Math.floor(Math.pow(i / barCount, 2) * (bufferLength * 0.65));

                // Obter valor da frequência e adicionar um pequeno fator aleatório
                // para garantir que todas as barras tenham movimento
                let value = dataArray[logIndex] || 0;
                if (value < 5) {
                    value += Math.random() * 8;
                }

                // Calcular altura da barra (máximo 85% da altura total)
                const barHeight = Math.max(4, (value / 255) * height * 0.85);

                // Posição X da barra
                const x = i * (barWidth + gap);

                // Posição Y (inverter para que as barras cresçam de baixo para cima)
                const y = height - barHeight;

                // Desenhar um gradiente para cada barra para efeito visual
                const gradient = ctx.createLinearGradient(0, y, 0, height);
                gradient.addColorStop(0, color);
                gradient.addColorStop(1, color + '88'); // Versão transparente da cor

                ctx.fillStyle = gradient;
                ctx.fillRect(x, y, barWidth, barHeight);

                // Arredondar o topo das barras
                ctx.beginPath();
                ctx.arc(x + barWidth / 2, y, barWidth / 2, 0, Math.PI * 2);
                ctx.fill();
            }
        };

        // Iniciar o loop de animação
        draw();
    };

    return (
        <Box
            className={className}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: `${height}px`,
                width: 'fit-content',
                margin: '0 auto',
                ...style,
            }}
        >
            <canvas ref={canvasRef} width={totalWidth} height={height} style={{ display: 'block' }} />
        </Box>
    );
};

export default AudioVisualizer;
